import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Settings, Hash, Building2, Shield, Zap, Banknote, MessageCircle, Truck, RefreshCw } from 'lucide-react';
import ThemeList from '../themes/ThemeList';
import JobSheetTemplateList from '../jobsheet-templates/JobSheetTemplateList';
import DocumentNumberSettings from '@/components/settings/DocumentNumberSettings';
import { DocumentType } from '@/services/documentNumberApi';
import CompanySettings from '@/components/settings/CompanySettings';
import RoleAccessSettings from '@/components/settings/RoleAccessSettings';
import CashSettlementSettings from '@/components/settings/CashSettlementSettings';
import WhatsAppTemplateSettings from '@/components/settings/WhatsAppTemplateSettings';
import DeliveryDueSettings from '@/components/settings/DeliveryDueSettings';
import RepeatedServiceSettings from '@/components/settings/RepeatedServiceSettings';
// import PointsSettings from '@/components/settings/PointsSettings'; // Points feature disabled

interface Tab {
  id: string;
  label: string;
  icon: typeof FileText;
  component: JSX.Element;
}

export default function SettingsMainPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabParam = searchParams.get('tab') || 'company';

  const tabs: Tab[] = [
    {
      id: 'company',
      label: 'Company',
      icon: Building2,
      component: <CompanySettings />,
    },
    {
      id: 'role-access',
      label: 'Role Access',
      icon: Shield,
      component: <RoleAccessSettings />,
    },
    {
      id: 'cash-settlement',
      label: 'Cash Settlement',
      icon: Banknote,
      component: <CashSettlementSettings />,
    },
    {
      id: 'delivery',
      label: 'Delivery',
      icon: Truck,
      component: <DeliveryDueSettings />,
    },
    {
      id: 'repeated-service',
      label: 'Repeated Service',
      icon: RefreshCw,
      component: <RepeatedServiceSettings />,
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      component: <WhatsAppTemplateSettings />,
    },
    // Points feature disabled
    // {
    //   id: 'points',
    //   label: 'Points',
    //   icon: Zap,
    //   component: <PointsSettings />,
    // },
    {
      id: 'invoice',
      label: 'Invoice',
      icon: FileText,
      component: (
        <div className="space-y-8">
          <DocumentNumberSettings fixedDocumentType={DocumentType.INVOICE} />
          {/* PDF Themes disabled
          <div className="border-t border-gray-200 pt-6">
            <ThemeList />
          </div>
          */}
        </div>
      ),
    },
    // {
    //   id: 'estimate',
    //   label: 'Estimate',
    //   icon: FileText,
    //   component: <ThemeList />,
    // },
    {
      id: 'job-sheet',
      label: 'Job Sheet',
      icon: FileText,
      component: (
        <div className="space-y-8">
          <DocumentNumberSettings allowedDocumentTypes={[DocumentType.JOB_SHEET, DocumentType.SERVICE_TICKET]} />
          {/* Job Sheet Templates disabled
          <div className="border-t border-gray-200 pt-6">
            <JobSheetTemplateList />
          </div>
          */}
        </div>
      ),
    },
    // Document Numbers tab disabled - Invoice in Invoice tab, Job Sheet & Service Ticket in Job Sheet tab
    // {
    //   id: 'document-numbers',
    //   label: 'Document Numbers',
    //   icon: Hash,
    //   component: <DocumentNumberSettings />,
    // },
  ];

  const activeTab = tabs.find((tab) => tab.id === activeTabParam) || tabs[0];

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  return (
    <div className="px-1 py-2 sm:p-4 md:p-6 space-y-3 sm:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-5 h-5 sm:w-7 sm:h-7" />
          Settings
        </h1>
        <p className="text-gray-600 text-sm sm:text-base mt-0.5">
          Manage company details, templates, and document settings
        </p>
      </div>

      {/* Tabbed Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab.id === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-3 sm:py-4 font-medium text-xs sm:text-sm whitespace-nowrap
                  border-b-2 transition-colors
                  ${
                    isActive
                      ? 'border-purple-600 text-purple-600 bg-purple-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-3 sm:p-6">
          {activeTab.component}
        </div>
      </div>
    </div>
  );
}
